import { type ISODate, PeriodType } from "@inrev/common";
import { hasPendingFinancialExtraction } from "@portal/api/financials";
import { DateTime } from "luxon";
import type { Severity } from "../utils/computeAccountUpdates";
import type { ComputeActionItemsInput } from "./computeActionItems";
import {
	daysUntilFyeExpiration,
	getLatestFyePeriod,
	nextRequiredFyeDate,
} from "./financialPeriods";
import type { ActionItem } from "./types";

type Update = {
	severity: Severity;
	label: string;
	description?: string;
};

type FyeUpdate = Update & { nextFyeDate: ISODate };

function computeFyePeriodUpdate(periodEndDate: ISODate): FyeUpdate | undefined {
	const periodDt = DateTime.fromISO(periodEndDate);
	const days = daysUntilFyeExpiration(periodEndDate);
	const nextFye = nextRequiredFyeDate(periodEndDate);
	const nextFyeYear = DateTime.fromISO(nextFye).year;

	if (days < 0) {
		return {
			severity: "warn",
			label: `${periodDt.year} FYE Financial Statement is expired`,
			description: `Please provide a ${nextFyeYear} FYE financial statement.`,
			nextFyeDate: nextFye,
		};
	}
	if (days < 30) {
		return {
			severity: "info",
			label: `${periodDt.toFormat("MMM d, yyyy")} FYE Financial Statement is expiring soon`,
			description: `Please provide a ${nextFyeYear} FYE financial statement.`,
			nextFyeDate: nextFye,
		};
	}
	return undefined;
}

function computeWipUpdate(
	latestWipStatement: NonNullable<ComputeActionItemsInput["latestWipStatement"]>,
): Update | undefined {
	const wipDt = DateTime.fromISO(latestWipStatement.date);
	const daysUntilExpiration = wipDt.plus({ months: 15 }).diffNow("days").days;

	if (daysUntilExpiration < 0) {
		return {
			severity: "warn",
			label: `${wipDt.toFormat("MMM d, yyyy")} WIP Statement is expired`,
			description: "Please provide a recent WIP statement",
		};
	}
	if (daysUntilExpiration < 30) {
		return {
			severity: "info",
			label: `${wipDt.toFormat("MMM d, yyyy")} WIP Statement is expiring soon`,
			description: "Please provide a recent WIP statement",
		};
	}
	return undefined;
}

export function computeExpirationItems(input: ComputeActionItemsInput): ActionItem[] {
	const { account, financialPeriods, financialExtractions, latestWipStatement } = input;

	const { modelSize } = account;

	if (modelSize == null || modelSize === "small") {
		return [];
	}

	const items: ActionItem[] = [];

	// Statement expiration prompts pause while a statement is classifying/extracting;
	// WIP expiration below is unrelated and stays visible.
	const statementInFlight = hasPendingFinancialExtraction(financialExtractions);

	const latestFye = getLatestFyePeriod(financialPeriods);
	if (latestFye && !statementInFlight) {
		const fsUpdate = computeFyePeriodUpdate(latestFye.periodEndDate);
		if (fsUpdate) {
			items.push({
				key: fsUpdate.severity === "warn" ? "fs-expired" : "fs-expiring",
				severity: fsUpdate.severity,
				label: fsUpdate.label,
				reason: fsUpdate.description,
				action: {
					type: "upload",
					periodEndDate: fsUpdate.nextFyeDate,
					periodType: PeriodType.Fye,
				},
				actionLabel: "Upload statement",
			});
		}
	}

	if (latestWipStatement) {
		const wipUpdate = computeWipUpdate(latestWipStatement);
		if (wipUpdate) {
			items.push({
				key: wipUpdate.severity === "warn" ? "wip-expired" : "wip-expiring",
				severity: wipUpdate.severity,
				label: wipUpdate.label,
				reason: wipUpdate.description,
				action: { type: "navigate", navigate: { tabId: "wip" } },
				actionLabel: "View WIP",
			});
		}
	}

	return items;
}
